/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import Link from 'next/link';
import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmDataAttributes.module.scss';

export const MdmDataAttributes = () => {
  const { t } = useInt();
  const gridRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(gridRef, { threshold: 0.1 });

  const categories = [
    {
      num: '01',
      title: t('Company registry data'),
      fields: [
        t('Legal name & previous names'),
        t('Registration number, VAT & tax IDs'),
        t('Legal form & incorporation date'),
        t('Company status'),
        t('Registered & trading addresses'),
        t('Registry source & filing link'),
      ],
    },
    {
      num: '02',
      title: t('Financials'),
      fields: [
        t('Filed revenue & turnover'),
        t('Assets, liabilities & net worth'),
        t('Profit & loss figures'),
        t('Filing history & latest accounts'),
        t('Currency & reporting period'),
        t('Multi-year financial trend'),
      ],
    },
    {
      num: '03',
      title: t('Firmographics'),
      fields: [
        t('Industry codes — SIC, NACE, NAICS'),
        t('Employee counts & size bands'),
        t('Company age & trading status'),
        t('Websites & digital footprint'),
        t('Phone numbers'),
        t('Geographic & jurisdiction data'),
      ],
    },
    {
      num: '04',
      title: t('Shareholders & ownership'),
      fields: [
        t('Shareholders & shareholdings'),
        t('Share classes & percentages'),
        t('Beneficial owners (PSCs / UBOs)'),
        t('Ownership percentages'),
        t('Ultimate beneficial owner chain'),
      ],
    },
    {
      num: '05',
      title: t('Group structure'),
      fields: [
        t('Immediate parent'),
        t('Global ultimate owner'),
        t('Subsidiaries & branches'),
        t('Corporate linkage across registries'),
        t('Cross-registry entity resolution'),
      ],
    },
    {
      num: '06',
      title: t('Directors & officers'),
      fields: [
        t('Directors & active officers'),
        t('Officer role & position'),
        t('Appointment & resignation dates'),
        t('Nationality & country of residence'),
        t('Date of birth (where filed)'),
        t('Occupation'),
      ],
    },
  ];

  return (
    <Section background="#0e1526" className={styles.root}>
      <div className={styles.dotGrid} aria-hidden="true" />
      <div className={styles.glow} aria-hidden="true" />

      <div className={cn(styles.head, hasIntersected && styles.headIn)}>
        <TitleBlock
          title={t('Every field on the resolved record, sourced')}
          description={t(
            'Up to 40 fields per company, grouped into six categories — each read from the registry it was filed with and carrying its source. Modelled values are labelled as modelled.',
          )}
          theme="dark"
          align="left"
        />
      </div>

      <div ref={gridRef} className={styles.grid}>
        {categories.map((cat, i) => (
          <div
            key={cat.num}
            className={cn(styles.cat, hasIntersected && styles.catIn)}
            style={{ '--i': i } as CSSProperties}
          >
            <div className={styles.catTop}>
              <span className={styles.catNum}>{cat.num}</span>
            </div>
            <h3 className={styles.catTitle}>{cat.title}</h3>
            <ul className={styles.fieldList}>
              {cat.fields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className={styles.note}>
        {t('Up to 40 fields per record. See the full')}{' '}
        <Link href="/data-dictionary" className={styles.noteLink}>
          {t('data dictionary')} <span className={styles.noteArrow}>→</span>
        </Link>
      </p>
    </Section>
  );
};
